import { Component, HostBinding } from '@angular/core'
import { Sort } from '@angular/material/sort'
import { Router } from '@angular/router'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ApplicationArea, DVM, DVMPlan, DVMPlanClient } from 'src/app/shared/services/ppv.api'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import tz from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(tz)

@Component({
   selector: 'dvm-tool-dashboard',
   templateUrl: './dvm-tool-dashboard.component.html',
   styleUrls: ['./dvm-tool-dashboard.component.scss'],
   standalone: false,
})
export class DVMToolDashboardComponent {
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   public isDVMToolAdmin = false
   public isRM = false
   public isOpsVP = false
   public isOpsDirector = false
   public userId: number = null

   public dvmPlans: DVMPlan[] = []
   public dvms: DVM[] = []

   public isDVMPlansLoaded = false
   public isDVMsLoaded = false

   public allRows = []
   public dashboardRows = []

   // filters (null = no filter)
   selectedDvmName: string | null = null
   selectedPractice: string | null = null
   selectedRmName: string | null = null

   // dropdown options
   dvmNameOptions: { label: string; value: string | null }[] = []
   practiceOptions: { label: string; value: string | null }[] = []
   rmNameOptions: { label: string; value: string | null }[] = []

   // current sort (default none)
   private currentSort: Sort = { active: '', direction: '' }
   private initialized = false

   constructor(private authMgr: AuthManager, private router: Router, private dvmPlanClient: DVMPlanClient) {
      this.authMgr.getCurrentUser().subscribe((user) => {
         this.userId = user.id

         //check if the user is a DVMToolAdmin
         this.isDVMToolAdmin = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['DVMToolAdmin'],
         })

         // is user an RM
         this.isRM = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['RM'],
         })
		 
		 // is user an OpsVP
         this.isOpsVP = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['OpsVP'],
         })
		 
		 // is user an OpsDirector
         this.isOpsDirector = this.authMgr.canAccess(user, {
            application: ApplicationArea.DVMTool,
            roleName: ['OpsDirector'],
         })

         if (this.isDVMToolAdmin) {
            this.getDVMPlansAll()
            this.getDVMsAll()
         } else if (this.isRM || this.isOpsVP || this.isOpsDirector) {
            this.getDVMPlansForRM(this.userId)
            this.getDVMsForRM(this.userId)
         }
      })
   }

   ngDoCheck() {
      if (!this.initialized && this.isDVMPlansLoaded && this.isDVMsLoaded) {
         this.updateDashboardRows()
         this.buildFilterOptions()
         this.applyFiltersSort()
         this.initialized = true
      }
   }

   onRowClick(row: DashboardRow): void {
      if (row) {
		  console.log(row)
         const localDate = dayjs.utc(row.lastUpdated).local()
         const formattedDate = localDate.format('MM/DD/YYYY')
         const formattedTime = localDate.format('hh:mm A')
         const lastUpdated = formattedDate + ' ' + formattedTime
         this.router.navigate(['/dvm-engagement', row.dvmId], {
            state: {
               lastUpdated: lastUpdated,
            },
         })
      }
   }

   onRowClickDVM(dvm: DVM): void {
      //console.log('Row clicked:', dvm)

      this.router.navigate(['/dvm-engagement/new'], { queryParams: { userId: dvm.dvmUserId } })
   }

   public getDVMPlansAll() {
      this.dvmPlanClient.getDVMPlansAll().subscribe({
         next: (data) => {
            this.dvmPlans = data
            this.isDVMPlansLoaded = true
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   public getDVMsAll() {
      this.dvmPlanClient.getDVMsAll().subscribe({
         next: (data) => {
            this.dvms = data
            this.isDVMsLoaded = true
            //console.log(this.dvms)
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   public getDVMPlansForRM(rmId: number) {
      this.dvmPlanClient.getDVMPlansForRM(rmId).subscribe({
         next: (data) => {
            this.dvmPlans = data

            this.updateDashboardRows()

            this.isDVMPlansLoaded = true
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   public getDVMsForRM(rmId: number) {
      this.dvmPlanClient.getDVMsForRM(rmId).subscribe({
         next: (data) => {
            this.dvms = data
            this.isDVMsLoaded = true
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   updateDashboardRows() {
      // console.log("updateDashboardRows()");

      //set from the existing plans
      const dashboardRowsFromExisting = this.dvmPlans
         .sort((a, b) => dayjs.utc(a.dateUpdated).local().toDate().getTime() - dayjs.utc(b.dateUpdated).local().toDate().getTime())
         .map((plan) => ({
            name: plan.dvmName || '',
            practice: plan.practiceName || '',
            lastUpdated: plan.dateUpdated
               ? dayjs.utc(plan.dateUpdated).local().format('MM/DD/YYYY') + ' ' + dayjs.utc(plan.dateUpdated).local().format('hh:mm A')
               : '',
            lastUpdatedDisplay: plan.dateUpdated ? dayjs.utc(plan.dateUpdated).local().format('MM/DD/YYYY') : '',
            hireDate: plan.dateHired ? dayjs.utc(plan.dateHired).local().toDate().toLocaleDateString() : '',
            rmName: plan.rmName || '',
            dvmId: plan.dvmId,
         }))

      //set from the dvms without plans
      const unmatchedDvms: DVM[] = this.dvms.filter((dvm) => !this.dvmPlans.some((dvmPlan) => dvm.dvmUserId === dvmPlan.dvmId)) 
      const dashboardRowsForNew: DashboardRow[] = unmatchedDvms.map((dvm) => ({
         name: dvm.dvmDisplayName || '',
         practice: dvm.practiceName || '',
         lastUpdated: 'NO PLAN',
         lastUpdatedDisplay: 'NO PLAN',
         hireDate: dvm.hireDate ? dayjs.utc(dvm.hireDate).local().toDate().toLocaleDateString() : '',
         rmName: dvm.rmDisplayName || '',
         dvmId: dvm.dvmUserId,
      }))

      const combinedDashboards = [...dashboardRowsForNew, ...dashboardRowsFromExisting]

      const uniqueRowsMap = new Map<string, DashboardRow>()

      combinedDashboards.forEach((row) => {
         const key = `${row.name}-${new Date(row.lastUpdated).toLocaleDateString()}`
         if (!uniqueRowsMap.has(key)) {
            uniqueRowsMap.set(key, row)
         }
      })

      const uniqueDashboardRowsFromExisting = Array.from(uniqueRowsMap.values())

      this.allRows = uniqueDashboardRowsFromExisting
      this.dashboardRows = uniqueDashboardRowsFromExisting
   }

   isValidDate(value: string): boolean {
      const date = new Date(value)
      return !isNaN(date.getTime()) // Returns false if it's an invalid date
   }

   onPracticeChange(val: any) {
      const actualVal = typeof val === 'string' ? val : (val as { value?: string | null })?.value ?? null
      this.selectedPractice = actualVal
      this.applyFiltersSort()
   }

   onDvmChange(val: any) {
      const actualVal = typeof val === 'string' ? val : (val as { value?: string | null })?.value ?? null
      this.selectedDvmName = actualVal
      this.applyFiltersSort()
   }

   onRmChange(val: any) {
      const actualVal = typeof val === 'string' ? val : (val as { value?: string | null })?.value ?? null
      this.selectedRmName = actualVal
      this.applyFiltersSort()
   }

   applyFiltersSort() {
      const byName = this.selectedDvmName
      const byPractice = this.selectedPractice
      const byRm = this.selectedRmName

      const filtered = this.allRows.filter(
         (r) => (byName ? r.name === byName : true) && (byPractice ? r.practice === byPractice : true) && (byRm ? r.rmName === byRm : true)
      )

      this.dashboardRows = this.sortArray(filtered, this.currentSort)
   }

   sortData(sort: Sort) {
      this.currentSort = sort
      this.dashboardRows = this.sortArray(this.dashboardRows, sort)
   }

   private sortArray(rows: DashboardRow[], sort: Sort): DashboardRow[] {
      if (!sort?.active || !sort.direction) return rows.slice()
      const isAsc = sort.direction === 'asc'

      const getVal = (r: DashboardRow) => {
         switch (sort.active) {
            case 'name':
               return r.name?.toLowerCase() ?? ''
            case 'practice':
               return r.practice?.toLowerCase() ?? ''
            case 'rmName':
               return r.rmName?.toLowerCase() ?? ''
            case 'lastUpdated':
               return r.lastUpdatedDisplay ?? null
            case 'hireDate': {
               const d = new Date(r.hireDate ?? '')
               return isNaN(d.getTime()) ? null : d.getTime()
            }
            default:
               return ''
         }
      }

      return rows.slice().sort((a, b) => {
         const av = getVal(a)
         const bv = getVal(b)

         if (av == null && bv == null) return 0
         if (av == null) return isAsc ? -1 : 1
         if (bv == null) return isAsc ? 1 : -1

         if (av < bv) return isAsc ? -1 : 1
         if (av > bv) return isAsc ? 1 : -1
         return 0
      })
   }

   private buildFilterOptions() {
      const uniq = (arr: (string | null | undefined)[]) => Array.from(new Set(arr.filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b))

      const names = uniq(this.allRows.map((r) => r.name))
      const practices = uniq(this.allRows.map((r) => r.practice))
      const rms = uniq(this.allRows.map((r) => r.rmName))

      this.dvmNameOptions = [...names.map((v) => ({ label: v, value: v }))]
      this.practiceOptions = [...practices.map((v) => ({ label: v, value: v }))]
      this.rmNameOptions = [...rms.map((v) => ({ label: v, value: v }))]
   }
}

export interface DashboardRow {
   name: string
   practice: string
   lastUpdated: string
   lastUpdatedDisplay?: string
   hireDate: string
   rmName: string
   dvmId: string
}
